import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import t from 'react-translate';
import RecordRTC, { StereoAudioRecorder } from 'recordrtc';
import { useSelector } from 'react-redux';

// redux
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { transcriptAudioRecording } from 'redux/actions/ai-role-play';
import { useAppDispatch } from 'redux/store';

import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import Spinner from 'shared/components/spinner';
import AudioStreamPreview from 'recording/components/audio-stream-preview';
import {
  MediaSourceType,
  requestMediaStream,
  stopMediaStreams,
} from 'recording/services/media-stream';
import { danger, gray2 } from 'styles/global_defaults/colors';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import NvTooltip from 'shared/components/nv-tooltip';
import AiConversationContext, { AudioRecordingStatus } from '../ai-conversation-modal/context';
import { config } from '../../../../../config/pendo.config.json';
import useTokenRefresh from './use-token-refresh';

type UseAudioTranscriptProps = {
  onTranscriptReceived: (transcript: string) => void;
};

const useAudioTranscript = ({ onTranscriptReceived }: UseAudioTranscriptProps) => {
  const {
    sessionData,
    setSessionData,
    audioRecordingStatus,
    setAudioRecordingStatus,
  } = useContext(AiConversationContext);

  const { ensureFreshToken } = useTokenRefresh({ sessionData, setSessionData });

  const { speechToTextEnabled } = useSelector(getCurrentInstitution);

  const dispatch = useAppDispatch();
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);
  const recorderRef = useRef<RecordRTC | null>(null);

  const isNotStarted = audioRecordingStatus === AudioRecordingStatus.NOT_STARTED;
  const isRecording = audioRecordingStatus === AudioRecordingStatus.RECORDING;
  const isTranscripting = audioRecordingStatus === AudioRecordingStatus.TRANSCRIPTING;
  const isRecordingInProgress = isRecording || isTranscripting;

  const stopAndCleanupMediaStream = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.destroy();
      recorderRef.current = null;
    }
    if (mediaStream) {
      stopMediaStreams(mediaStream);
      setMediaStream(null);
    }
  }, [mediaStream]);

  // Store the cleanup function in a ref to avoid re-running on every mediaStream change
  const cleanupRef = useRef(stopAndCleanupMediaStream);
  cleanupRef.current = stopAndCleanupMediaStream;

  // Cleanup media stream only on unmount
  useEffect(() => () => {
    cleanupRef.current();
  }, []);

  const handleStartAudioRecording = async () => {
    try {
      const stream = await requestMediaStream(MediaSourceType.MIC);

      // Initialize RecordRTC with WAV format
      recorderRef.current = new RecordRTC(stream, {
        type: 'audio',
        mimeType: 'audio/wav',
        recorderType: StereoAudioRecorder,
        desiredSampRate: 16000,
        numberOfAudioChannels: 1,
        disableLogs: true,
      });
      recorderRef.current.startRecording();

      setMediaStream(stream);
      setAudioRecordingStatus(AudioRecordingStatus.RECORDING);
    } catch (error) {
      // Check if permission was denied
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setIsMicPermissionDenied(true);
      } else {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
          customColor: danger,
        }));
      }
    }
  };

  const handleCancelAudioRecording = () => {
    stopAndCleanupMediaStream();
    setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
  };

  const handleDictateRecording = async () => {
    if (!recorderRef.current) {
      return;
    }

    setAudioRecordingStatus(AudioRecordingStatus.TRANSCRIPTING);

    recorderRef.current.stopRecording(async () => {
      try {
        const jwtToken = await ensureFreshToken();

        // Get audio as base64 data URL directly from RecordRTC
        const dataURL = await new Promise<string>((resolve) => {
          recorderRef.current?.getDataURL((url) => resolve(url));
        });

        if (!dataURL) {
          throw new Error('Failed to get audio data');
        }

        // Extract base64 string from data URL (remove "data:audio/wav;base64," prefix)
        const audioBase64 = dataURL.split(',')[1];

        const result = await dispatch(transcriptAudioRecording({
          jwtToken,
          aiApiEndpoint: sessionData.aiApiEndpoint,
          audioBase64,
          languageLocale: sessionData.languageLocale,
        })).unwrap();

        if (result?.trim()) {
          onTranscriptReceived(result);
        }

        setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
      } catch (error) {
        dispatch(addAlertMessage({
          type: AlertMessageType.WARNING,
          header: t.FORM.OOPS(),
          message: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SOMETHING_WENT_WRONG(),
          customColor: danger,
        }));
        setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
      } finally {
        stopAndCleanupMediaStream();
      }
    });
  };

  const isDictationDisabled = !speechToTextEnabled || isMicPermissionDenied;

  const getRecordingTooltip = () => {
    if (!speechToTextEnabled) {
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.DICTATION_NOT_ENABLED();
    }
    if (isMicPermissionDenied) {
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.MIC_PERMISSION_DENIED();
    }
    return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.DICTATE();
  };

  const renderStartRecordingButton = () => (
    <NvTooltip enabled text={getRecordingTooltip()}>
      <ClickableContainer
        onClick={handleStartAudioRecording}
        className={`ml-auto mr-2 ${isDictationDisabled ? 'text-gray-4 bg-gray-6' : 'text-gray-2 bg-gray-7 hover-text-black'} rounded-circle record-actions justify-content-center`}
        disabled={isDictationDisabled}
        aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.START_AUDIO_RECORDING()}
        data-qa={config.pendo.activities.aiRolePlay.conversationModal.startAudioRecording}
      >
        <NvIcon
          icon='audio-recording'
          size='small'
          className='d-flex align-items-center'
        />
      </ClickableContainer>
    </NvTooltip>
  );

  const renderCancelRecordingButton = () => (
    <ClickableContainer
      onClick={handleCancelAudioRecording}
      className={`mr-2 ${isTranscripting ? 'text-gray-4' : 'text-black'} bg-gray-7 rounded-circle record-actions justify-content-center`}
      disabled={isTranscripting}
      aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.ABORT_RECORDING()}
      data-qa={config.pendo.activities.aiRolePlay.conversationModal.abortRecording}
    >
      <NvIcon
        icon='close'
        size='smallest'
        className='d-flex align-items-center'
      />
    </ClickableContainer>
  );

  const renderTranscriptingSpinner = () => (
    <div className='mr-2 d-flex align-items-center justify-content-center bg-gray-7 text-gray-4 rounded-circle record-actions'>
      <Spinner size={16} />
    </div>
  );

  const renderConfirmRecordingButton = () => (
    <ClickableContainer
      onClick={handleDictateRecording}
      className='mr-2 text-black bg-gray-7 dictate-button rounded-circle record-actions justify-content-center border-primary'
      aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.STOP_AND_DICTATE_AUDIO_RECORDING()}
      data-qa={config.pendo.activities.aiRolePlay.conversationModal.stopAndDictateAudioRecording}
    >
      <NvIcon
        icon='check'
        size='smallest'
        className='d-flex align-items-center'
      />
    </ClickableContainer>
  );

  const renderAudioWaveform = () => (
    <div className='audio-waveform-container d-flex align-items-center justify-content-center px-2'>
      {mediaStream && (
        <AudioStreamPreview
          mediaStream={mediaStream}
          isPlaying={isRecording}
          color={gray2}
        />
      )}
    </div>
  );

  const renderRecordingControls = () => (
    <Fragment>
      {renderCancelRecordingButton()}
      {isTranscripting && renderTranscriptingSpinner()}
      {isRecording && renderConfirmRecordingButton()}
    </Fragment>
  );

  return {
    isNotStarted,
    isRecording,
    isTranscripting,
    isRecordingInProgress,
    handleDictateRecording,
    renderStartRecordingButton,
    renderRecordingControls,
    renderAudioWaveform,
  };
};

export default useAudioTranscript;
